import React, { useState } from 'react';
import { ATTRIBUTES } from '../../scoring';

const styles = {
    container: { marginTop: '20px' },
    roundSection: { marginBottom: '30px' },
    roundTitle: {
        fontSize: '18px', fontWeight: 'bold', color: '#4b2e83',
        marginBottom: '10px', borderBottom: '2px solid #4b2e83', paddingBottom: '4px',
    },
    toggleRow: {
        display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px',
    },
    toggleBtn: (active) => ({
        padding: '8px 20px',
        backgroundColor: active ? '#4b2e83' : '#e0e0e0',
        color: active ? '#fff' : '#333',
        border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold',
    }),
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px',
    },
    th: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '8px 6px',
        textAlign: 'right', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap',
    },
    thLeft: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '8px 6px',
        textAlign: 'left', borderBottom: '2px solid #ddd',
    },
    td: {
        padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee',
    },
    tdLeft: {
        padding: '6px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 'bold',
    },
    noData: {
        textAlign: 'center', color: '#999', padding: '20px', fontStyle: 'italic',
    },
    segBadge: {
        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
        backgroundColor: '#e8e0f0', color: '#4b2e83', fontSize: '11px',
        marginRight: '4px', marginBottom: '2px',
    },
};

const fmt = (val) => `$${val.toLocaleString()}`;

const AdvertisingSpendView = ({ teams, rounds: roundsProp }) => {
    const [viewMode, setViewMode] = useState('summary');
    const teamNames = teams.map(t => t.teamName).sort();
    const rounds = roundsProp || [1, 2, 3];

    const getAdData = (team, roundIndex) => {
        const roundData = team.teamData.rounds?.[roundIndex];
        if (!roundData?.is_submitted) return null;
        const ad = roundData.advertising || {};
        const perf = ad.performance_ads || {};
        const brand = ad.brand_ads || {};
        const perfTotal = Object.values(perf).reduce((s, v) => s + (v || 0), 0);
        const brandTotal = Object.values(brand).reduce((s, v) => s + (v || 0), 0);
        return {
            segments: ad.segments || [],
            performance: perf,
            brand: brand,
            perfTotal,
            brandTotal,
            total: perfTotal + brandTotal,
        };
    };

    if (viewMode === 'summary') {
        return (
            <div style={styles.container}>
                <div style={styles.toggleRow}>
                    <button style={styles.toggleBtn(true)} onClick={() => setViewMode('summary')}>Summary</button>
                    <button style={styles.toggleBtn(false)} onClick={() => setViewMode('detail')}>Attribute Breakdown</button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.thLeft}>Team</th>
                                {rounds.map(r => (
                                    <React.Fragment key={r}>
                                        <th style={styles.th}>R{r} Perf</th>
                                        <th style={styles.th}>R{r} Brand</th>
                                        <th style={styles.th}>R{r} Total</th>
                                    </React.Fragment>
                                ))}
                                <th style={styles.th}>Grand Total</th>
                                <th style={{ ...styles.thLeft, textAlign: 'left' }}>Segments Targeted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {teamNames.map(name => {
                                const team = teams.find(t => t.teamName === name);
                                let grandTotal = 0;
                                const latestSegments = [];
                                return (
                                    <tr key={name}>
                                        <td style={styles.tdLeft}>{name}</td>
                                        {rounds.map((r) => {
                                            const ad = getAdData(team, r - 1);
                                            if (ad) {
                                                grandTotal += ad.total;
                                                if (ad.segments.length > 0) {
                                                    latestSegments.length = 0;
                                                    latestSegments.push(...ad.segments);
                                                }
                                            }
                                            return (
                                                <React.Fragment key={r}>
                                                    <td style={styles.td}>{ad ? fmt(ad.perfTotal) : '-'}</td>
                                                    <td style={styles.td}>{ad ? fmt(ad.brandTotal) : '-'}</td>
                                                    <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                        {ad ? fmt(ad.total) : '-'}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                        <td style={{ ...styles.td, fontWeight: 'bold' }}>{fmt(grandTotal)}</td>
                                        <td style={{ ...styles.td, textAlign: 'left', maxWidth: '200px' }}>
                                            {latestSegments.length > 0
                                                ? latestSegments.map(s => (
                                                    <span key={s} style={styles.segBadge}>{s}</span>
                                                ))
                                                : <span style={{ color: '#999' }}>-</span>
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Detail view - per-round attribute breakdown
    return (
        <div style={styles.container}>
            <div style={styles.toggleRow}>
                <button style={styles.toggleBtn(false)} onClick={() => setViewMode('summary')}>Summary</button>
                <button style={styles.toggleBtn(true)} onClick={() => setViewMode('detail')}>Attribute Breakdown</button>
            </div>

            {rounds.map((round) => {
                const hasData = teamNames.some(n => {
                    const team = teams.find(t => t.teamName === n);
                    return getAdData(team, round - 1) !== null;
                });

                return (
                    <div key={round} style={styles.roundSection}>
                        <div style={styles.roundTitle}>Round {round}</div>
                        {!hasData ? (
                            <p style={styles.noData}>No data for Round {round}</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.thLeft}>Team</th>
                                            <th style={styles.th}>Type</th>
                                            {ATTRIBUTES.map(attr => (
                                                <th key={attr} style={styles.th}>{attr}</th>
                                            ))}
                                            <th style={styles.th}>Broad</th>
                                            <th style={styles.th}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teamNames.map(name => {
                                            const team = teams.find(t => t.teamName === name);
                                            const ad = getAdData(team, round - 1);
                                            if (!ad) return null;
                                            return (
                                                <React.Fragment key={name}>
                                                    <tr>
                                                        <td style={{ ...styles.tdLeft, borderBottom: 'none' }} rowSpan={2}>{name}</td>
                                                        <td style={{ ...styles.td, textAlign: 'left', fontSize: '11px', borderBottom: 'none' }}>Perf</td>
                                                        {ATTRIBUTES.map(attr => (
                                                            <td key={attr} style={{ ...styles.td, borderBottom: 'none' }}>
                                                                {ad.performance[attr] ? fmt(ad.performance[attr]) : '-'}
                                                            </td>
                                                        ))}
                                                        <td style={{ ...styles.td, borderBottom: 'none' }}>
                                                            {ad.performance.Broad ? fmt(ad.performance.Broad) : '-'}
                                                        </td>
                                                        <td style={{ ...styles.td, borderBottom: 'none', fontWeight: 'bold' }}>
                                                            {fmt(ad.perfTotal)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ ...styles.td, textAlign: 'left', fontSize: '11px' }}>Brand</td>
                                                        {ATTRIBUTES.map(attr => (
                                                            <td key={attr} style={styles.td}>
                                                                {ad.brand[attr] ? fmt(ad.brand[attr]) : '-'}
                                                            </td>
                                                        ))}
                                                        <td style={styles.td}>
                                                            {ad.brand.Broad ? fmt(ad.brand.Broad) : '-'}
                                                        </td>
                                                        <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                            {fmt(ad.brandTotal)}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AdvertisingSpendView;
